<template>
  <div class="rb-ob-form rb-match-page rb-tx-page">
    <header><h1 class="rb-ob-title">{{ form ? form.title : $t('intake.treatment.orsTitle') }}</h1></header>
    <div v-if="form" class="rb-tx-meta">
      <span><strong>{{ form.sessionLabel }}</strong></span>
      <span>{{ $t('intake.treatment.metaVersion') }} <strong>{{ form.contentVersion }}</strong></span>
    </div>
    <p v-if="form" class="rb-tx-intro">{{ form.intro }}</p>
    <p v-if="form && form.respondentNote" class="rb-tx-intro">{{ form.respondentNote }}</p>
    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="loading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>
    <p v-else-if="!form" class="rb-ob-status" role="status">{{ formStatus || $t('intake.treatment.noPendingAssessment') }}</p>

    <fieldset v-for="item in (form && !isSkipped ? form.items : [])" :key="item.key" class="rb-tx-ruler">
      <legend>{{ item.label }}</legend>
      <div class="rb-tx-ruler-track" dir="ltr">
        <input v-model.number="scores[item.key]" type="range" :min="form.scale.min" :max="form.scale.max" :step="form.scale.step" :aria-valuetext="$t('intake.treatment.rulerAriaUnset')">
        <div class="rb-tx-ruler-anchors" dir="rtl"><span>{{ item.leftAnchor }}</span><span>{{ item.rightAnchor }}</span></div>
      </div>
      <label class="rb-tx-confirm"><input v-model="confirmed[item.key]" type="checkbox"><span>{{ $t('intake.treatment.confirmAnswer') }}</span></label>
    </fieldset>

    <div class="rb-ob-actions">
      <template v-if="form && !isSkipped">
        <button class="rb-ob-submit" type="button" :disabled="submitting || form.completed" @click="submit">{{ submitting ? $t('intake.portal.loading') : $t('intake.treatment.orsSubmit') }}</button>
        <button v-if="!form.completed" class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="incomplete">{{ $t('intake.treatment.orsIncomplete') }}</button>
      </template>
      <router-link v-else class="rb-ob-submit" :to="{name:'portalHome',params:{lang:lang()}}">{{ $t('intake.treatment.backToPortal') }}</router-link>
      <p class="rb-ob-status" :class="{'is-error': !!formStatus && !!form}" role="status" aria-live="polite">{{ form ? formStatus : '' }}</p>
      <div class="rb-ob-trust"><p>{{ $t('intake.trustPath') }}</p><p>{{ $t('authUi.familyNote') }}</p></div>
    </div>
  </div>
</template>
<script>
import {
  hasAuthToken,
  nextAssessmentRoute,
  rememberLastAssessment,
  resolvePendingTreatmentAssessment,
  skipTreatmentAssessment,
  submitTreatmentAssessment,
  treatmentAssessmentErrorKey,
} from '@/intake/portalApi'

export default {
  name: 'OutcomeOrsStep',
  data() {
    return {loading: true, submitting: false, needsLogin: false, form: null, formStatus: '', scores: {}, confirmed: {}}
  },
  computed: {
    isSkipped() { return !!(this.form && this.form.obligationStatus === 'skipped') },
  },
  mounted() { this.bootstrap() },
  methods: {
    lang() { return this.$route.params.lang || 'fa' },
    fail(e) { return this.$t(treatmentAssessmentErrorKey(e)) },
    bootstrap() {
      this.loading = true
      this.form = null
      this.formStatus = ''
      this.scores = {}
      this.confirmed = {}
      this.needsLogin = !hasAuthToken()
      if (this.needsLogin) { this.loading = false; return }
      const sessionId = this.$route.query.sessionId || null
      return resolvePendingTreatmentAssessment(sessionId, 'ORS').then(async (form) => {
        if (!form) {
          this.formStatus = this.$t('intake.treatment.noPendingAssessment')
          return
        }
        const target = nextAssessmentRoute(this.lang(), form)
        if (target.name !== 'intakeOutcomeOrs') {
          return this.$router.replace(target)
        }
        if (String(sessionId || '') !== String(form.sessionId)) {
          await this.$router.replace({...target, query: {...this.$route.query, ...target.query}})
        }
        this.form = form
        const scores = {}
        const confirmed = {}
        ;(form.items || []).forEach((item) => {
          scores[item.key] = form.scale.initialValue
          confirmed[item.key] = false
        })
        this.scores = scores
        this.confirmed = confirmed
        if (form.obligationStatus === 'skipped') this.formStatus = this.$t('intake.treatment.orsAlreadySkipped')
        else if (form.completed) this.formStatus = this.$t('intake.treatment.orsAlreadyDone')
      }).catch((e) => {
        this.form = null
        this.formStatus = this.fail(e)
      }).finally(() => { this.loading = false })
    },
    submit() {
      if (!this.form || this.submitting || this.isSkipped || this.form.completed) return
      const keys = (this.form.items || []).map((item) => item.key)
      if (!keys.every((key) => this.confirmed[key])) {
        this.formStatus = this.$t('intake.treatment.confirmRequired')
        return
      }
      this.submitting = true
      this.formStatus = ''
      return submitTreatmentAssessment(this.form, keys.map((key) => this.scores[key])).then((result) => {
        rememberLastAssessment({kind: 'ors', assessmentId: result.assessmentId, sessionId: this.form.sessionId, status: 'completed', at: Date.now()})
        this.$router.push({name: 'intakeAssessDone', params: {lang: this.lang()}, query: {kind: 'ors', sessionId: String(this.form.sessionId)}})
      }).catch((e) => { this.formStatus = this.fail(e) }).finally(() => { this.submitting = false })
    },
    incomplete() {
      if (!this.form) { this.$router.push({name: 'portalHome', params: {lang: this.lang()}}); return }
      if (this.submitting || this.isSkipped || this.form.completed) return
      this.submitting = true
      this.formStatus = ''
      return skipTreatmentAssessment(this.form).then(() => {
        this.$router.push({name: 'intakeAssessMiss', params: {lang: this.lang()}, query: {sessionId: String(this.form.sessionId)}})
      }).catch((e) => { this.formStatus = this.fail(e) }).finally(() => { this.submitting = false })
    },
  },
}
</script>
